import { civilFromEpochDay, daysInMonth, epochDayFromCivil } from './calendar';

/**
 * The water column through the year (M19): temperature, salinity, oxygen and
 * the three nutrients at the World Ocean Atlas's standard depths, month by
 * month, for the aquarium and for each of the twelve regions; and the depth of
 * the mixed layer, from de Boyer Montégut's climatology over the same cells
 * (M32).
 *
 * The tables are climatology, built by `scripts/data/woa.ts` into
 * `data/water/woa23.ts`: the mean year, the 1991 to 2020 normal for
 * temperature and salinity. A given year's departure from it (El Niño, a
 * marine heatwave) is Phase 1's next step and is drawn on the climate stream;
 * nothing here draws.
 *
 * **A day between two months.** Each month has a value at its middle, and a
 * day is the straight-line blend of the two middles either side of it, read
 * at noon. The middles' values are not the Atlas's monthly means but the
 * values that make each month's days average to them (Killworth 1996,
 * `scripts/data/woaBuild.ts`); straight lines through the means themselves
 * cut the corners of the seasonal cycle, by up to 0.30 °C a month at the
 * surface in the high bands (MEASURED, M19).
 */

export type Tracer = 'temperature' | 'salinity' | 'oxygen' | 'nitrate' | 'phosphate' | 'silicate';

export const TRACERS: readonly Tracer[] = ['temperature', 'salinity', 'oxygen', 'nitrate', 'phosphate', 'silicate'];

/** One place's water column through the year. */
export interface WaterClimatology {
  /** Where the profiles come from, as the readout names it. */
  source: string;
  /**
   * Each tracer's profile at the middle of each month, January first, at the
   * standard depths from the surface down (`STANDARD_DEPTHS_M`), as far as the
   * Atlas's monthly field and the place's water go: the values that make each
   * month's days average to the Atlas's monthly mean (see above).
   */
  monthly: Readonly<Record<Tracer, ReadonlyArray<readonly number[]>>>;
  /** Below the monthly profiles, the annual mean, the same in every month, as deep as the place's water goes. */
  deep: Readonly<Record<Tracer, readonly number[]>>;
  /** Where the mixed layer comes from, as the readout names it. */
  mixedLayerSource: string;
  /**
   * The mixed layer's depth at the middle of each month, m: de Boyer
   * Montégut's climatology (M32, version 2023), found in each single profile
   * by 0.03 kg/m³ above the density at 10 m and mapped at one degree,
   * averaged over the place's cells by area, and made mean-preserving like
   * the profiles. (The Atlas's own, found in its monthly mean profiles by
   * Monterey and Levitus's criterion, read 6 m deeper than Station ALOHA's
   * casts, its RMS 1.4 m larger: M29, M32.)
   */
  mixedLayerM: readonly number[];
}

/** Noon, as a fraction of a day, and the last month of the year (definitions). */
const NOON = 0.5;
const DECEMBER = 12;

/** The two months whose middles a day lies between (0 for January), and how far the day is toward the second. */
export interface MonthBlend {
  readonly from: number;
  readonly to: number;
  readonly toward: number;
  /** The years the two months are in, which a monthly record reads by (`sstRecord.ts`; review M25: it worked them out again). */
  readonly fromYear: number;
  readonly toYear: number;
}

/**
 * A day's blend, the same for every place: the tick finds it once a day and
 * every place reads its water with it. MEASURED (M19, `npm run sim -- --years
 * 10 --seed 42` and a million calls timed): the first form, with closures and
 * tuples, took 2.0 µs a call and was found twice for each of thirteen places,
 * 63 µs a tick; this one takes 0.4 µs, once, and the tick 3.9 to 5.7 µs.
 */
export function monthBlend(epochDay: number): MonthBlend {
  const noon = epochDay + NOON;
  const { year, month } = civilFromEpochDay(epochDay);
  let y0 = year;
  let m0 = month;
  let a = middleOf(year, month);
  if (noon < a) {
    y0 = month === 1 ? year - 1 : year;
    m0 = month === 1 ? DECEMBER : month - 1;
    a = middleOf(y0, m0);
  }
  const y1 = m0 === DECEMBER ? y0 + 1 : y0;
  const m1 = m0 === DECEMBER ? 1 : m0 + 1;
  return { from: m0 - 1, to: m1 - 1, toward: (noon - a) / (middleOf(y1, m1) - a), fromYear: y0, toYear: y1 };
}

/** A month's middle, as an epoch day and a fraction. */
function middleOf(year: number, month: number): number {
  return epochDayFromCivil(year, month, 1) + daysInMonth(year, month) / 2;
}

/**
 * A tracer's profile on a day, from the surface down, at the standard depths.
 * Only the tests read whole profiles yet; the environment reads the surface
 * (`surfaceOn`). Phase 1's light at depth and the hindcast read them (review
 * M20).
 */
export function profileOn(water: WaterClimatology, tracer: Tracer, day: MonthBlend): number[] {
  const { from, to, toward } = day;
  const a = water.monthly[tracer][from] as readonly number[];
  const b = water.monthly[tracer][to] as readonly number[];
  return [...a.map((v, k) => v + ((b[k] as number) - v) * toward), ...water.deep[tracer]];
}

/** A tracer's value at the surface on a day. */
export function surfaceOn(water: WaterClimatology, tracer: Tracer, day: MonthBlend): number {
  const { from, to, toward } = day;
  const a = water.monthly[tracer][from]?.[0] as number;
  const b = water.monthly[tracer][to]?.[0] as number;
  return a + (b - a) * toward;
}

/** The mixed layer's depth on a day, m. */
export function mixedLayerOn(water: WaterClimatology, day: MonthBlend): number {
  const { from, to, toward } = day;
  const a = water.mixedLayerM[from] as number;
  const b = water.mixedLayerM[to] as number;
  return a + (b - a) * toward;
}

/**
 * How many standard levels lie in water `depthM` deep: those shallower than
 * the floor. The aquarium's 3D grid is its 1 km columns (`seafloor.ts`) cut
 * at these levels, each cell the column's water at one standard depth (M19).
 * Only the tests call it yet: Phase 1's light at depth is the first to build
 * the grid, and kept here, tested, so it is built one way (review M20).
 */
export function levelsInWater(depthM: number, depthsM: readonly number[]): number {
  let n = 0;
  while (n < depthsM.length && (depthsM[n] as number) < depthM) n++;
  return n;
}
